import {
    type UscCashPriceType,
    getUscCashPriceFromVehicle,
    getUscCashPriceValue,
    showUscPrice,
} from "../../shared-logic/utils/uscPriceUtils";
import { ExternalPriceType, ExternalPriceValueType } from "./externalPriceTypes";
import type { VehicleType } from "../../shared-logic/types/UscCommonTypes";
import { type DfPricingSourceCode, DF_DELIVERY_COST_KEY } from "../../shared-logic/utils/dfConstants";
import { formatPriceIntl } from "../../../common-deprecated/priceUtils";
import { getUscLabel } from "./labels";
import { COMPONENT_USED_STOCK_CARS_PDF } from "../../../shared-logic/server/components";
import type { UscStateType } from "../redux/store";

// ----------------------------------------------------------------------
// This file contains price utils specific to the USC component.
// Any pricing logic relevant to the entire UCL (Including compare/car-filter) should be moved to the common uscPriceUtils file.
// ----------------------------------------------------------------------

// This type should be used whenever a price sourced from UscCashPriceType is rendered (vehicle/accessory/...).
export type VisualUscPriceType = {
    cashPrice: UscCashPriceType;
    cashLoading: boolean;
    monthlyPrice: number;
    monthlyPriceFormatted: string; // This can be optional depending on usage. Total price for example doesnt use this as it uses Flexibility Matrix formatting.
    monthlyLoading: boolean;
};

/**
 * Retrieve a total price to render in the frontend.
 */
export const getTotalPriceSelector =
    (forceExternalPrice: boolean = false) =>
    (state: UscStateType): VisualUscPriceType => {
        const { externalPrice, vehicle, finance, uscSettings, commonSettings } = state;
        const externalPriceAvailable = uscSettings.enableExternalPrice;
        const isPDF = commonSettings.component === COMPONENT_USED_STOCK_CARS_PDF;

        // Retrieve base cash price.
        const cashPrice: UscCashPriceType = getUscCashPriceFromVehicle(state.vehicle.result);

        // Use externalPrice if it is shown or if the force param is used.
        // "Forcing" external price usage means that even though it is not shown it is still used for total price calculations (for example insurance/finance/lead data).
        const useExternalPrice = externalPrice.show || (externalPriceAvailable && forceExternalPrice);
        if (useExternalPrice && externalPrice.data) {
            // The externalPriceTypes.car TMEF price should be the total price of the car (including any additional services or configs).
            // We don't need to do any additional calculations here as TMEF should cover all of that.
            const getTmefCashPrice = (valueType: ExternalPriceValueType): number => {
                const tmefCarPrice = externalPrice.data?.Prices.find((price) => price.Type === ExternalPriceType.Car);
                return tmefCarPrice?.Values.find((priceValue) => priceValue.Type === valueType)?.Amount || 0;
            };

            // TMEF Promotions are not supported for now.
            cashPrice.cashPriceInclVat = getTmefCashPrice(ExternalPriceValueType.InclVat);
            cashPrice.cashPriceExclVat = getTmefCashPrice(ExternalPriceValueType.ExclVat);
        } else if (
            // Non-TMEF deliveryCost case: DF data can contain a deliveryCost if it needs to be sourced from some other place instead of TMEF.
            // Before adding the deliveryCost to the total price, check if it is localised as it is possible to have a deliveryCost present even when TMEF should be used.
            vehicle.result?.vehicleValue.deliveryCost && // deliveryCost can be both null and 0, in both cases the cost should be hidden (TAF requirement).
            !!uscSettings.externalPriceConfig.find((config) => config.key === DF_DELIVERY_COST_KEY) &&
            cashPrice.cashPriceInclVat
        ) {
            cashPrice.cashPriceInclVat += vehicle.result?.vehicleValue.deliveryCost;
        }

        return {
            cashPrice,
            monthlyPrice: finance.info ? finance.monthlyPrice : 0,
            monthlyPriceFormatted: "", // Not relevant for the total price as total price goes through FM formatting.
            // forceClientRefresh means that we don't want to render the prices in the serverside response, in this case we show the loading bar.
            // However, we should still show the prices in the PDF as the PDF is generated serverside.
            cashLoading:
                !vehicle.result || externalPrice.fetching || (!!vehicle.result?.price?.forceClientRefresh && !isPDF),
            monthlyLoading: finance.fetching || externalPrice.fetching,
        };
    };

/**
 * Used to render a "simple" price which is the total monthly/cash price without any additional disclaimers.
 * This uses getTotalPriceSelector as well, it just returns a simple formatted price string to render instead.
 * Relevant for the financeToggle and mobile navigation.
 *
 * @param useDefaultMonthlyFormat - Set to true to use the uscDefaultMonthlyRateFormat label for the monthly price.
 */
export const getSimplifiedTotalPriceSelector =
    (useDefaultMonthlyFormat: boolean = false) =>
    (state: UscStateType): { monthly: string; cash: string; monthlyLoading?: boolean; cashLoading?: boolean } => {
        const { commonSettings } = state;
        const { monthlyPrice, cashPrice, cashLoading, monthlyLoading } = getTotalPriceSelector()(state);

        const isPdf = commonSettings.component === COMPONENT_USED_STOCK_CARS_PDF;

        let formattedMonthlyPrice = formatPriceIntl(commonSettings, monthlyPrice, {}, false).primaryPrice;
        const formattedCashPrice = formatPriceIntl(
            commonSettings,
            getUscCashPriceValue(cashPrice),
            {},
            true,
        ).primaryPrice;

        const loadingLabel = getUscLabel(state, "uscLoading");

        if (useDefaultMonthlyFormat) {
            formattedMonthlyPrice = getUscLabel(state, "uscDefaultMonthlyRateFormat", { price: formattedMonthlyPrice });
        }

        return {
            monthly: monthlyLoading && !isPdf ? loadingLabel : formattedMonthlyPrice,
            cash: cashLoading && !isPdf ? loadingLabel : formattedCashPrice,
            monthlyLoading,
            cashLoading,
        };
    };

export const getVehiclePriceSource = (vehicle: VehicleType | null): DfPricingSourceCode | null =>
    vehicle?.price?.pricingSource?.code || null;

/**
 * Simple selector for easy access to showPrice logic (Allows for easy updates/refactor work in the future).
 */
export const showUscPriceSelector = (state: UscStateType): boolean =>
    showUscPrice(getVehiclePriceSource(state.vehicle.result));
